import React, { createContext, useCallback, useContext, useState } from 'react';

const DisclaimerContext = createContext(null);

export const useDisclaimer = () => {
  const ctx = useContext(DisclaimerContext);
  if (!ctx) {
    throw new Error('useDisclaimer must be used within DisclaimerProvider');
  }
  return ctx;
};

export const DisclaimerProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openDisclaimer = useCallback(() => setOpen(true), []);
  const closeDisclaimer = useCallback(() => setOpen(false), []);

  return (
    <DisclaimerContext.Provider value={{ openDisclaimer, closeDisclaimer }}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 rc-fade-in">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={closeDisclaimer}
            role="presentation"
          />
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-[640px] w-full max-h-[85vh] flex flex-col border border-slate-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="disclaimer-title"
          >
            <div className="flex items-start justify-between px-7 py-5 border-b border-slate-200">
              <div>
                <div className="text-[11px] font-semibold tracking-wider uppercase text-amber-700">Important</div>
                <h2 id="disclaimer-title" className="text-[20px] font-medium tracking-tight text-slate-900 mt-0.5">
                  Disclaimer &amp; terms of use
                </h2>
              </div>
              <button
                type="button"
                onClick={closeDisclaimer}
                className="w-8 h-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition flex items-center justify-center flex-shrink-0"
                aria-label="Close disclaimer"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                  <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-7 py-5 text-[13.5px] text-slate-700 leading-relaxed space-y-5">
              <section>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">Not financial advice</h3>
                <p>
                  This retirement planning calculator is an educational tool that provides general information only. It
                  does <strong>not</strong> constitute financial, investment, tax, or legal advice. We are not
                  authorised by the Financial Conduct Authority (FCA) to provide financial advice.
                </p>
              </section>

              <section>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">For educational purposes only</h3>
                <p>
                  This calculator is designed to help you understand potential retirement scenarios based on
                  assumptions <strong>you</strong> provide. It is not a substitute for professional financial planning
                  advice tailored to your individual circumstances.
                </p>
              </section>

              <section>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">No warranty of accuracy</h3>
                <p>
                  While we make reasonable efforts to ensure accuracy, we make{' '}
                  <strong>no warranties or guarantees</strong> about the accuracy, completeness, or reliability of any
                  calculations, projections, or information provided. Results are estimates only.
                </p>
              </section>

              <section>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">Your responsibility</h3>
                <p>You are solely responsible for:</p>
                <ul className="list-disc pl-5 mt-1.5 space-y-1">
                  <li>The assumptions you input</li>
                  <li>Verifying the accuracy of results</li>
                  <li>Any decisions you make based on the information provided</li>
                  <li>Seeking professional advice before making financial decisions</li>
                </ul>
              </section>

              <section>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">No liability</h3>
                <p>
                  To the fullest extent permitted by law, we accept no liability for any loss, damage, or harm arising
                  from your use of this calculator, any reliance on its results, any errors or omissions in calculations,
                  or investment decisions made based on this tool.
                </p>
              </section>

              <section>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">Assumptions and limitations</h3>
                <p>
                  This calculator uses tax rates and rules current as of the 2025/26 tax year, which may change. It makes
                  simplified assumptions that may not reflect reality, cannot predict future investment returns or
                  inflation, and does not account for all personal circumstances.
                </p>
              </section>

              <section>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">Investment and tax risks</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Investments can go down as well as up</li>
                  <li>You may get back less than you invested</li>
                  <li>Past performance does not guarantee future results</li>
                  <li>Tax rules and allowances may change</li>
                  <li>State pension amounts and eligibility may change</li>
                </ul>
              </section>

              <section>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">Seek professional advice</h3>
                <p>
                  For personalised retirement planning, consult an FCA-authorised independent financial adviser, a
                  qualified tax adviser, and/or a solicitor for estate planning.
                </p>
                <p className="mt-1.5">
                  Find an FCA-authorised adviser at{' '}
                  <a
                    href="https://www.unbiased.co.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                  >
                    unbiased.co.uk
                  </a>
                  .
                </p>
              </section>

              <section>
                <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">Changes &amp; governing law</h3>
                <p>
                  We may update, modify, or discontinue this calculator at any time without notice. These terms are
                  governed by English law; any disputes are subject to the exclusive jurisdiction of the courts of
                  England and Wales.
                </p>
              </section>

              <section className="pt-2 border-t border-slate-200">
                <p className="text-slate-500 text-[12.5px]">
                  By using this calculator, you acknowledge that you have read, understood, and agree to these terms.
                </p>
                <p className="text-slate-400 text-[11.5px] mt-1">Last updated: May 2026</p>
              </section>
            </div>
            <div className="px-7 py-4 border-t border-slate-200 bg-slate-50/50 rounded-b-2xl flex justify-end">
              <button
                type="button"
                onClick={closeDisclaimer}
                className="px-4 py-2 rounded-full bg-slate-900 text-white text-[13px] font-medium hover:bg-slate-800 transition"
              >
                I understand
              </button>
            </div>
          </div>
        </div>
      )}
    </DisclaimerContext.Provider>
  );
};
